import {
  APP_3in1,
  APP_M1,
  APP_SRC,
  APP_URL_INDENTIFIER,
  COMMON_SSO_ACCESS_TOKEN,
  SOURCE,
  WEB_LOGGED_IN,
  WEB_NON_LOGGED_IN,
  WEB_NON_LOGGED_IN_IDENTIFIED,
  WEB_VIEW_MODE,
  WEB_VIEW_MODE_ON,
} from "../constants/sso.constants";
import {
  cryptoAPICalledData,
  decrypt,
  decryptAESCBC256,
  encrypt,
  encryptAESECB128,
  encryptWebform,
  generateSalt,
} from "./crypto";
import { APP_PROMPTS } from "../constants/pwa.constants";
import sessionData from "../mock/loggedIn-mock.json";
import { isOpenInApp } from "./pwa-utils";
import { LogUtils } from "./log-utils";
const cache = new WeakMap();

export type SSOURLConfigs = {
  fullUrl: string;
  newUrl: URL;
  ssoProduct: string[] | null;
  ssoModule: string | null;
  ssoResume: string | null;
  ssoFormPostUrl: string | null;
};

/**
 * Checks if a string or array has content
 * @param {string | Array<unknown>} value - The value to check
 * @returns {boolean} True if the value is not null, not undefined, and has length greater than 0
 * @example
 * doesExist("hello") // returns true
 * doesExist([1, 2, 3]) // returns true
 * doesExist("") // returns false
 * doesExist([]) // returns false
 */
export const doesExist = (
  value: string | null | undefined | Array<unknown>,
) => {
  return value !== null && value !== undefined && value?.length > 0;
};

/**
 * Checks if an object has properties
 * @param {unknown} value - The object to check
 * @returns {boolean} True if the value is not null, not undefined, and has at least one property
 * @example
 * isNotEmptyObject({ key: "value" }) // returns true
 * isNotEmptyObject({}) // returns false
 */
export const isNotEmptyObject = (value: unknown = {}) => {
  return value !== null && value !== undefined && Object.keys(value).length > 0;
};

/**
 * Determines if a given URL path should be handled by Next.js routing
 * @param {string} pageUrl - The URL path to check
 * @returns {boolean} True if the URL should be handled by Next.js, false otherwise
 * @example
 * isNextPage("/investments/stock-name") // returns true
 * isNextPage("/anything-for-now/") // returns false
 */
export const isNextPage = (pageUrl: string) => {
  // add regexps to match for urls
  const matchPatterns = [/^\/?investments\/[^/]+-share-price$/i];

  const notMatchPatterns = [/^\/anything-for-now\/$/];

  const matches = matchPatterns.some((pattern) => pattern.test(pageUrl));

  const notMatches = notMatchPatterns.some((pattern) => pattern.test(pageUrl));

  return matches && !notMatches;
};

/**
 * Checks if code is running in a browser environment
 * @returns {boolean} True if running in a browser, false otherwise
 * @example
 * if (isBrowser()) {
 *   // Execute browser-specific code
 * }
 */
export const isBrowser = () => {
  return "undefined" !== typeof window;
};

/**
 * Gets a URL parameter value from the current window location
 * @param {string} paramsValue - The name of the parameter to retrieve
 * @returns {string | null} The value of the parameter if found, null otherwise
 * @example
 * // For URL: https://example.com?tab=profile
 * getParams("tab") // returns "profile"
 */
export const getParams = (paramsValue: string): string | null => {
  const url = new URL(window.location.href);
  const params = url.searchParams;
  return params.get(paramsValue);
};

/**
 * Smoothly scrolls the window to a specified position
 * @param {number} [top=0] - The Y position to scroll to (in pixels)
 * @param {ScrollBehavior} [behavior="smooth"] - The scroll behavior ("auto" | "smooth")
 * @returns {void}
 * @example
 * scrollTo(0) // Scrolls to top smoothly
 * scrollTo(500, "auto") // Jumps to 500px position
 */
export const scrollTo = (
  top: number = 0,
  behavior: ScrollBehavior | undefined = "smooth",
): void => {
  if (isBrowser()) {
    window.scrollTo({ top, behavior });
  }
};

/**
 * Converts an object with keys like "item0", "item1", etc.,
 * into a sorted array based on numeric suffix of the keys.
 *
 * @param {Object} unorderedObj - Object with keys like "item0", "item2", etc.
 * @param {string} keyPrefix - Common prefix used in the keys (e.g., "item")
 * @returns {Array} - Sorted array of object values
 */
export const sortObjectByKeySuffix = (
  unorderedObj: Record<string, unknown>,
  keyPrefix = "item",
) => {
  if (!unorderedObj || typeof unorderedObj !== "object") return [];

  return Object.keys(unorderedObj)
    .sort((a, b) => {
      const numA = parseInt(a.replace(keyPrefix, ""), 10);
      const numB = parseInt(b.replace(keyPrefix, ""), 10);
      return numA - numB;
    })
    .map((key) => unorderedObj[key]);
};
export const formatDateWithFullYear = (input: string) => {
  const year = input?.slice(0, 4);
  const month = input?.slice(4);
  const date = new Date(parseInt(year), parseInt(month) - 1);
  const formatted = date.toLocaleString("default", {
    month: "short",
    year: "numeric",
  });
  return formatted;
};

/**
 * Sets a cookie with the specified name, value, and optional parameters
 * @param {string} name - The name of the cookie
 * @param {string} value - The value of the cookie
 * @param {number} [days] - Optional number of days until the cookie expires
 * @param {string} [path="/"] - Optional path for the cookie
 */
export const setCookie = (
  cookieName: string,
  time: number,
  value: string,
): void => {
  const date = new Date();
  let expires: string;
  if (time > 0) {
    const cookieTime = time;
    date.setTime(date.getTime() + cookieTime * 60 * 1000);
    expires = `expires=${date.toUTCString()}`;
    document.cookie = `${cookieName}=${value}; path=/; domain=.bajajfinserv.in;  ${expires};`;
  } else {
    document.cookie = `${cookieName}=${value}; path=/; domain=.bajajfinserv.in;`;
  }
};

/**
 * Deletes a cookie with the specified name
 * @param {string} name - The name of the cookie to delete
 * @param {string} [path="/"] - Optional path of the cookie
 */
export const deleteCookie = (
  name: string,
  path: string = "/",
  domain: string = ".bajajfinserv.in",
): void => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=${path}; domain=${domain}`;
};

/**
 * Gets the value of a cookie by name
 * @param {string} name - The name of the cookie to retrieve
 * @returns {string | null} The cookie value if found, null otherwise
 */
export const getCookie = (name: string): string | null => {
  if (!isBrowser()) return "";
  const matches = document.cookie.match(
    new RegExp(
      `(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1")}=([^;]*)`,
    ),
  );
  return matches?.[1] ? decodeURIComponent(matches[1]) : null;
};

export const toTitleCase = (str: string) => {
  return str
    ?.toLowerCase()
    ?.split(" ")
    ?.map((word) => word?.charAt(0).toUpperCase() + word.slice(1))
    ?.join(" ");
};
/**
 * Generates a sorted array of entries from an object based on componentPostion property
 * @param obj - The input object containing components with componentPostion property
 * @returns An array of sorted entries where each entry is a tuple of [key, value]
 */
export const generateSortedComponentList = (obj: Record<string, any>) => {
  if(obj)
  {
    const sortedList = Object.entries(obj).sort(
      ([, a], [, b]) => Number(a?.componentPosition) - Number(b?.componentPosition),
    );
    return sortedList;
  }
  return []
};

/**
 * Decodes HTML entities and special characters in a text string.
 *
 * @param text - The input string containing HTML entities and special characters to decode
 * @returns The decoded string with HTML entities and special characters converted to their actual representations
 *
 * @remarks
 * This function handles the following conversions:
 * - Removes double escaped newlines (\\r\\n)
 * - Removes regular newlines (\r\n)
 * - &quot; to "
 * - &nbsp; to space
 * - &lt; to <
 * - &gt; to >
 * - &amp; to &
 * - \u003C to <
 * - \u003E to >
 */
export const decodeHtmlEntities = (text: string | undefined | null) => {
  if (!text) {
    return '';
  }
  return (
    text
      .replace(/&quot;/g, '"')
      .replace(/&nbsp;/g, " ")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&")
      .replace(/\\u003C/g, "<")
      .replace(/\\u003E/g, ">")

      // Convert escaped newlines to real ones first
      .replace(/\\r\\n/g, "\n")
      .replace(/\\n/g, "\n")
      .replace(/\\r/g, "")
      .replace(/\r/g, "")

      // Remove empty or meaningless paragraphs
      .replace(/<p>\s*<\/p>/gi, "")

      // Replace real newlines (now cleaned) with <br/> only when not next to a tag
      .replace(/(?<!>)\n(?!<)/g, "<br/>")

      // Reduce multiple <br/> to a single one
      .replace(/(<br\s*\/?>\s*){2,}/gi, "<br/>")

      // add line break and ensure exactly two asterisks
      .replace(/([a-z0-9>])\s*\*{2,}\s*Disclaimer/gi, "$1<br/>**Disclaimer")

      .trim()
  );
};

export const extractPlainText = (html: string): string => {
  if (!html) return "";

  return html
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<\/?[a-zA-Z0-9-]+(?:\s[^>]*)?>/g, "") // Remove tags
    .replace(/\s+/g, " ") // Normalize whitespace
    .trim();
};

export const getChildByNodeName = (parent: any, nodeName: string): any[] => {
  return getSingleChildByNodeName(parent, nodeName)?.children ?? [];
};

export const getsearchPlatform = (authType: string) => {
  let searchPlatform: string;
  switch (authType) {
    case "I":
      searchPlatform = WEB_NON_LOGGED_IN_IDENTIFIED;
      break;
    case "A":
      searchPlatform = WEB_LOGGED_IN;
      break;
    default:
      searchPlatform = WEB_NON_LOGGED_IN;
  }
  return searchPlatform;
};

export const webFormData = (searchKey: string): Record<string, unknown> => {
  const searchPlatform = getsearchPlatform(getCookie("authType") ?? "");
  return {
    custType: getCookie("custType"),
    icorsOfferType: null,
    offerId: null,
    offerAmount: null,
    product: null,
    userEnteredAmount: null,
    cart: false,
    cardDesign: null,
    loginTimestamp: null,
    applicationID: null,
    prodCode: null,
    fasID: null,
    principleOutstandingAmt: null,
    principleOutStandingAmount: null,
    keyword_searched_searchWeb: searchKey,
    search_platform: searchPlatform,
    source: SOURCE,
  };
};

const redirectToWebFormNonLoggedin = async (
  url: string,
  form: Record<string, unknown>,
) => {
  const ssoRequest = {
    sso: {
      mobileNo: "",
      custId: "",
      accessToken: "",
      callbackUrl: "",
    },
    utm: null,
    myAccount: null,
    form,
  };
  const searchData = await encryptAESECB128(JSON.stringify({ ssoRequest }));
  const pwaUrl = new URL(url);
  pwaUrl.searchParams.append(
    "searchData",
    encodeURIComponent(searchData.toString()),
  );
  window.open(pwaUrl, "_self");
};

export const getCustomerDemogFromApp = () => {
  const windowWithTarsier = window as Window & {
    tarsier?: { invokePrompt: (prompt: string, force?: boolean) => unknown };
  };
  const customerDemog = JSON.parse(
    (windowWithTarsier.tarsier?.invokePrompt(
      APP_PROMPTS.CUSTOMER_DEMOG_DETAILS,
      false,
    ) as string | undefined) ?? "",
  );
  return customerDemog;
};

export const isValidURL = (url: string) => {
  try {
    return Boolean(new URL(url));
  } catch (e) {
    return false;
  }
};

export const getFullUrl = (url: string, options = { hash: true }): string => {
  if (!isValidURL(url)) {
    const currentUrl = new URL(window.location.href);
    const newUrl = new URL(
      `${currentUrl.protocol}//${currentUrl.hostname}${url}`,
    );

    // Convert to string once for reuse
    let urlString = newUrl.toString();

    if (!options.hash) {
      return urlString.replace(newUrl.hash, "");
    }

    const src = getParams(APP_SRC);
    if (src === APP_M1) {
      const separator = urlString.includes("?") ? "&" : "?";
      const queryParam = `source=${src}`;
      urlString = `${urlString}${separator}${queryParam}`;
    }

    return urlString;
  }
  return url;
};
async function getSSOData(callbackUrl: string) {
  const { mobileNo, custId } = await getSessionData();
  const accessToken = localStorage.getItem(COMMON_SSO_ACCESS_TOKEN);
  const fullUrl = getFullUrl(callbackUrl, { hash: false });
  const redirectionTime = `${Math.round(new Date().getTime() / 1000)}`;
  const src = getParams(APP_SRC);
  const source = src === APP_M1 ? APP_M1 : APP_3in1;
  return {
    mobileNo,
    accessToken,
    callbackUrl: fullUrl,
    custId,
    redirectionTime,
    source,
  };
}

export const getPageType = (metaName: string): string => {
  // Check if running in a browser environment
  if (typeof document === "undefined" || !metaName) return "";
  try {
    const metaTag = document.querySelector(`meta[name="${metaName}"]`);
    if (!metaTag) {
      return "";
    }
    if (metaTag instanceof HTMLMetaElement) {
      return metaTag.content ?? "";
    }
    return metaTag.getAttribute("content") ?? "";
  } catch (error) {
    LogUtils.error(`Error getting page type for ${metaName}:`, error);
    return "";
  }
};

/**
 * This function retrieves UTM parameters from a cookie named "campaignCookie".
 * It also retrieves the value of a cookie named "lastClick" and the current window's URL.
 *
 * @returns An object containing the UTM parameters, the source URL, and the last click value.
 *
 * If any error occurs during the process (like the cookie not being present or not being a valid JSON), the function returns undefined.
 */
export function getUtmParamsFromCookie() {
  try {
    const cookie = sessionData?.campaignCookie;
    const lastClick = sessionData?.lastClick;
    const bflpageType =
      sessionData?.bfl_page_type ?? getPageType("bfl_page_type");
    return {
      utmSource: cookie?.utm_source,
      utmCampaign: cookie?.utm_campaign,
      utmMedium: cookie?.utm_medium,
      utmKeyword: cookie?.utm_keyword,
      utmContent: cookie?.utm_content,
      sourceUrl: window.location.href,
      bflpageType: bflpageType,
      lastClick,
    };
  } catch (e) {
    return undefined;
  }
}

export function getTnCVersion() {
  const tncVersionNumber = getPageType("tncVersionNumber");
  if (tncVersionNumber.length !== 0) {
    return {
      tncFlag: "1",
      tncVersionNumber: tncVersionNumber.toUpperCase(),
    };
  }
  return undefined;
}

export async function redirectToWebform(
  linkUrl: string,
  form: Record<string, unknown>,
  inAppFormLink?: string,
): Promise<void> {
  const sso = await getSSOData(linkUrl);
  const utm = getUtmParamsFromCookie();
  const tnc = getTnCVersion();

  const ssoObject = {
    ssoRequest: {
      sso,
      utm,
      form,
      tnc,
    },
  };
  const ssoObjectSerialized = JSON.stringify(ssoObject);
  const ssoObjectEncrypted = await encryptWebform(ssoObjectSerialized);
  const currentUrl = window.location.href;
  const currentUrlEncrypted = await encryptWebform(currentUrl);
  const src = getParams(APP_SRC);
  if (isOpenInApp() && inAppFormLink && form.applicationID && src !== APP_M1) {
    const { IN_APP_REDIRECTION, BAJAJ_SUPER_APP } = APP_PROMPTS;
    const inAppRedirectionPrompt = `${IN_APP_REDIRECTION}${BAJAJ_SUPER_APP}${inAppFormLink}/${String(form.applicationID)}`;
    const windowWithTarsier = window as Window & {
      tarsier?: { invokePrompt: (prompt: string, force?: boolean) => unknown };
    };
    windowWithTarsier.tarsier?.invokePrompt(inAppRedirectionPrompt, true);
    return;
  }
  const url = new URL(sso.callbackUrl);
  url.searchParams.append("reqData", encodeURIComponent(ssoObjectEncrypted));
  url.searchParams.append("errorUrl", encodeURIComponent(currentUrlEncrypted));
  if (form.isVc) {
    window.location.replace(url);
  } else {
    window.open(url, "_self");
  }
}

export const redirectSearchWebForm = (
  url: string,
  searchKey: string,
  isLoggedIn: boolean,
) => {
  const form = webFormData(searchKey);
  if (isLoggedIn) {
    redirectToWebform(url, form);
  } else {
    redirectToWebFormNonLoggedin(url, form);
  }
};
/**
 * Helper function to get a single child by node name from a parent object
 * @param {any} parent - The parent object containing children array
 * @param {string} nodeName - The nodeName to search for
 * @returns {any} The first child object with matching nodeName, or undefined if not found
 * @example
 * getSingleChildByNodeName(globalHeader, "headerlogo")
 * getSingleChildByNodeName(bottomnavigationmobileStrip, "bottomNavigationMenu")
 */
export const getSingleChildByNodeName = (
  parent: any,
  nodeName: string,
): any => {
  return parent?.children?.find((item: any) =>
    item?.nodeName?.startsWith(nodeName),
  );
};

export const createCacheAsync = async <
  // biome-ignore lint/suspicious/noExplicitAny: cannot know type of args AOT
  Fn extends (...args: any[]) => any,
>(
  key: string,
  fn: Fn,
  ...args: Parameters<Fn>
): Promise<ReturnType<Fn>> => {
  let cachedResults = cache.get(fn);
  if (!cachedResults) {
    cachedResults = {};
    cache.set(fn, cachedResults);
  }
  if (!cachedResults[key]) {
    const result = fn(...args).catch(() => {
      cachedResults[key] = undefined;
    });
    cachedResults[key] = result;
  }
  return cachedResults[key];
};

export const finalHandler: any = async (href: string) => {
  const callbackUrl = href;
  const formData = {
    cart: true,
  };
  redirectToWebform(callbackUrl.toString(), formData);
};

export const ssoHandlerMap = {
  mere: (data: any) => {
    // Handle Mere SSO logic
    LogUtils.log("Handling Mere SSO with data:", data);
  },
  default: (data: any) => {
    // Default SSO handler
    LogUtils.log("Handling default SSO with data:", data);
  },
  cart: finalHandler,
};

const callHandler = (mapping: SSOURLConfigs, data: any) => {
  try {
    if (data?.callMereHandler) {
      ssoHandlerMap.mere(data);
    } else if (mapping.ssoModule && mapping.ssoModule in ssoHandlerMap) {
      // @ts-ignore
      ssoHandlerMap[mapping.ssoModule](data);
    } else {
      ssoHandlerMap.default(data);
    }
  } catch (error) {
    LogUtils.error(error);
    throw error;
  }
};

export const unixEpoch = (): string =>
  `${Math.round(new Date().getTime() / 1000)}`;

export async function redirectToDncPage() {
  const cryptoData = await cryptoAPICalledData();
  if (cryptoData) {
    window.open(cryptoData?.DNC_PAGE_URL, "_self");
  }
}

export async function getVersionedCallbackUrl(mapping: SSOURLConfigs) {
  return mapping.newUrl;
}

export const parseSessionCookie = (cookie: string) => getCookie(cookie) ?? null;
export function readCookie(name: string): string | null {
  const cookieString = document.cookie;
  if (!cookieString) {
    return null;
  }

  const cookies = cookieString.split(";");
  for (const cookie of cookies) {
    const trimmedCookie = cookie.trim();
    const separatorIndex = trimmedCookie.indexOf("=");
    const cookieName = trimmedCookie.slice(0, separatorIndex);
    const cookieValue = trimmedCookie.slice(separatorIndex + 1);
    if (cookieName === name) {
      return cookieValue;
    }
  }

  return null;
}

export const getUserDetailsCookiesDecrypted = async () => {
  try {
    const bmallUserDetailsEncrypted = readCookie("bmall_user_details");
    if (bmallUserDetailsEncrypted) {
      const cryptoData = await cryptoAPICalledData();
      if (
        !cryptoData?.REQ_ENC_DEC_PASS_PHRASE_EXP ||
        !cryptoData?.REQ_ENC_DEC_IV_EXP
      ) {
        LogUtils.error("Missing crypto data for decryption");
        return null;
      }
      const { REQ_ENC_DEC_PASS_PHRASE_EXP, REQ_ENC_DEC_IV_EXP } = cryptoData;
      const bmallUserDetailsPerpetual: any = await decryptAESCBC256(
        bmallUserDetailsEncrypted,
        REQ_ENC_DEC_PASS_PHRASE_EXP,
        REQ_ENC_DEC_IV_EXP,
      );
      return JSON.parse(bmallUserDetailsPerpetual);
    }
    return null;
  } catch (error) {
    LogUtils.error("Error decrypting user details:", error);
    return null;
  }
};

export interface UserSessionDataTypes {
  custType: string;
  custId?: string;
  mobileNo: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  dob?: string;
  newDate: string;
  requestId: string;
  email: string;
  isOffers: string | boolean;
  city: string;
  dealerId: string;
  dealerName: string;
  cndFlag?: boolean;
  reKycFlag?: string;
  kycRenewalDate?: string;
  pinCode?: string;
  indivCorpFlag?: string;
  easyEmiProduct?: string;
  rekycflag?: string;
  kycProcess?: string;
}
// Get session data using key
export const getSessionData = async (): Promise<UserSessionDataTypes> => {
  const uniqueDigits = { uniqueDigits: "" };
  const sessionObj: UserSessionDataTypes | null = getCookie("userDetails")
    ? (JSON.parse(getCookie("userDetails") || "{}") as UserSessionDataTypes)
    : null;
  if (sessionObj) {
    const encryptedMobileNo = sessionObj.mobileNo;
    const encryptedDob = sessionObj.dob;
    const encryptedEmail = sessionObj.email;
    const salt = sessionObj.newDate;
    let decryptedMobileNo;
    if (uniqueDigits.uniqueDigits) {
      decryptedMobileNo = uniqueDigits.uniqueDigits;
    } else if (encryptedMobileNo) {
      decryptedMobileNo = await decrypt(encryptedMobileNo, salt);
    } else {
      decryptedMobileNo = "";
    }
    const decryptedDob = encryptedDob ? await decrypt(encryptedDob, salt) : "";
    const decryptedEmail = encryptedEmail
      ? await decrypt(encryptedEmail, salt)
      : "";
    // If mobileNo is not available in app, it will be fetched from customerdemog prompt
    const mobileNo =
      isOpenInApp() && !decryptedMobileNo
        ? getCustomerDemogFromApp().mobileNo
        : decryptedMobileNo;
    return {
      ...sessionObj,
      mobileNo: `${mobileNo}`,
      dob: `${decryptedDob}`,
      email: `${decryptedEmail}`,
    };
  }
  return {
    custType: "NTB",
    mobileNo: "",
    fullName: "",
    dob: "",
    firstName: "",
    lastName: "",
    newDate: "",
    requestId: "",
    email: "",
    isOffers: "",
    city: "",
    dealerId: "",
    dealerName: "",
    cndFlag: false, // Do Not Call (DNC) flag, variable name changed to cndFlag for security
    reKycFlag: "",
    kycRenewalDate: "",
    pinCode: "",
    indivCorpFlag: "",
  };
};

export const addStash = (url: string) => {
  let redirectionUrl: string;
  if (!url) {
    redirectionUrl = "/";
  } else if (url.startsWith("/")) {
    redirectionUrl = url;
  } else {
    redirectionUrl = `/${url}`;
  }
  return isOpenInApp() ? `/app-v1${redirectionUrl}` : redirectionUrl;
};

export const openRedirectURL = (url?: string, target?: string): void => {
	if (!url) return;
	const finalTarget = target ?? "_blank";
	const isApp = checkIsAppUrl(url);
	if(isApp) {
    openUrlInApp(url,finalTarget);
  } 
  else {
    openUrlInBrowser(url,finalTarget);
  }
};

const checkIsAppUrl = (url: string): boolean =>
	url.includes(APP_URL_INDENTIFIER) ||
	getParams(APP_SRC) === APP_3in1 ||
	getParams(APP_SRC) === APP_M1 ||
	getCookie(WEB_VIEW_MODE) === WEB_VIEW_MODE_ON;

const invokeAppPrompt = (prompt: string, force?: boolean) => {
  const windowWithTarsier = window as Window & {
    tarsier?: { invokePrompt: (appPrompt: string, shouldForce?: boolean) => unknown };
  };
  if (force === undefined) {
    windowWithTarsier.tarsier?.invokePrompt(prompt);
    return;
  }
  windowWithTarsier.tarsier?.invokePrompt(prompt, force);
};

export const openSearchInApp = (type: "BAR" | "MIC", force = true) => {
  const prompt = `${APP_PROMPTS.IN_APP_REDIRECTION}${APP_PROMPTS.BAJAJ_SUPER_APP}${APP_PROMPTS.APP_SEARCH}/${type}`;
  invokeAppPrompt(
    prompt,
    force,
  );
};

const openUrlInApp = (url: string, target: string) => {
  if (!url?.includes("bajajfinserv.in") && url?.startsWith("https:")) {
    const transformedUrl = generateUrlPath(url);
    window.prompt(
      `openInAppWebViewActivity://{"url":"${transformedUrl}","title":"Bajaj Finserv"}`
    );
  } else if (url?.includes("bajajsuperapp") && !url?.startsWith("https:")) {
    invokeAppPrompt(`${APP_PROMPTS.IN_APP_REDIRECTION}${url}`);
  } else {
    openUrlInBrowser(url,target);
  }
};

const openUrlInBrowser = (url: string, target: string) => {
  window.open(url, target);
};

export const generateUrlPath = (path: string) => {
	const currentDomain = window.location.origin;
	const currentHref = window.location.href;

	const versionMatch = currentHref?.match(/app-v(\d+)/);
	const version = versionMatch ? versionMatch[0] : null;

	const formattedPath = version ? `${version}/${path}` : path;

	return `${currentDomain}/${formattedPath}`;
};

export async function redirectToMyAccount(
  ssoPostUrl: string,
  linkUrl: string,
): Promise<void> {
  const sso = await getSSOData(linkUrl);
  const utm = getUtmParamsFromCookie();
  const { custType } = await getSessionData();
  const authType = getCookie("authType");
  const custFlag = custType === "NTB" ? "IN" : getCookie("custFlag");
  const ssoObject = {
    ssoRequest: {
      sso,
      utm,
      myAccount: {
        custType,
        custFlag,
        authType,
      },
    },
  };
  const ssoObjectSerialized = JSON.stringify(ssoObject);
  const salt = await generateSalt();
  const ssoObjectEncrypted = await encrypt(ssoObjectSerialized, salt);
  const form = document.createElement("form");
  form.method = "POST";
  form.action = getFullUrl(ssoPostUrl);

  const reqDataInput = document.createElement("input");
  reqDataInput.name = "reqData";
  reqDataInput.value = ssoObjectEncrypted;
  reqDataInput.type = "hidden";
  form.appendChild(reqDataInput);

  const reqKeyInput = document.createElement("input");
  reqKeyInput.name = "reqKey";
  reqKeyInput.value = salt;
  reqKeyInput.type = "hidden";
  form.appendChild(reqKeyInput);

  const errorUrl = document.createElement("input");
  errorUrl.name = "errorUrl";
  const encryptedErrorUrl = await encrypt(window.location.href, salt);
  errorUrl.value = encodeURIComponent(encryptedErrorUrl);
  errorUrl.type = "hidden";
  form.appendChild(errorUrl);

  document.body.append(form);
  form.submit();
}
